import { ChangeDetectionStrategy, Component, inject, signal, Signal, Type } from '@angular/core';

import { take } from 'rxjs';

import { RtComponentOutlet } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import {
  PositionDetailsDialogData,
  PositionDetailsTab,
  TellentPosition,
} from '@core/tellent-positions-types';
import {
  ConnectedOverlayDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogRef,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';

import { PositionsDialogsService } from '../services/positions-dialogs.service';
import { PositionDetailsDescriptionComponent } from './tabs/position-details-description/position-details-description.component';
import { PositionDetailsEmployeesComponent } from './tabs/position-details-employees/position-details-employees.component';
import { PositionDetailsSkillsComponent } from './tabs/position-details-skills/position-details-skills.component';

@Component({
  selector: 'tlc-position-details-dialog',
  templateUrl: './position-details-dialog.component.html',
  styleUrls: ['./position-details-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    IconComponent,
    TranslatePipe,
    TabsComponent,
    TabComponent,
    IconButtonComponent,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    OverlayOriginDirective,
    RtComponentOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PositionsDialogsService],
})
export class PositionDetailsDialogComponent implements DialogComponentI<
  PositionDetailsDialogData,
  any
> {
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);
  private readonly positionsDialogsService = inject(PositionsDialogsService);
  private readonly dialogRef = inject(DialogRef);

  protected readonly DropdownWidth = DropdownWidth;
  protected readonly PositionDetailsTab = PositionDetailsTab;
  protected readonly activeTab = signal<PositionDetailsTab>(PositionDetailsTab.Employees);
  protected readonly componentMap = new Map<PositionDetailsTab, Type<unknown>>([
    [PositionDetailsTab.Employees, PositionDetailsEmployeesComponent],
    [PositionDetailsTab.Description, PositionDetailsDescriptionComponent],
    [PositionDetailsTab.Skills, PositionDetailsSkillsComponent],
  ]);

  public positionData: Signal<TellentPosition | null>;

  private positionGuid = signal<string>('');

  public setDialogData(data: PositionDetailsDialogData, triggerUpdate: boolean): void {
    this.positionGuid.set(data.positionGuid);
    this.positionsDataFacade.fetchOne(data.positionGuid);
    this.positionData = this.positionsDataFacade.getPositionById(data.positionGuid);
  }

  public toggleMoreActionsDropdown(dropdown: ConnectedOverlayDirective): void {
    dropdown.open = !dropdown.open;
  }

  public renamePosition(): void {
    this.positionsDialogsService
      .openRenamePositionDialog(this.positionGuid())
      .pipe(take(1))
      .subscribe();
  }

  public deletePosition(): void {
    const position = this.positionData();
    if (!position) return;

    this.positionsDialogsService
      .openDeletePositionDialog(this.positionData()!)
      .pipe(take(1))
      .subscribe(result => {
        if (result.success) this.dialogRef.close(result);
      });
  }
}
